<script setup>
import { Link as InertiaLink } from '@inertiajs/vue3';
import { useSidebarStore } from '@/stores/sidebarStore.js';
import { computed } from 'vue';
import { IconApps, IconDatabasePlus, IconMenu2, IconSearch, IconUserPlus, IconId } from '@tabler/icons-vue';
import Dropdown from '@/components/Dropdown.vue';
import DropdownItemGroup from '@/components/DropdownItemGroup.vue';
import DropdownItem from '@/components/DropdownItem.vue';
import { useUser } from '@/composables/useUser';
import UserDropdown from '@/components/UserDropdown.vue';
import { useGlobalSearch } from '@/composables/useGlobalSearch';

const store = useSidebarStore();

const sidebarOpen = computed({
    get() {
        return store.open;
    },
    set(newValue) {
        store.toggleSidebar(newValue);
    },
});

const user = useUser();
const { openSearch } = useGlobalSearch();
</script>

<template>
    <header
        class="fixed left-0 top-0 z-60 flex h-14 w-full items-center justify-between bg-primary-500 md:h-16"
    >
        <div class="px-4 md:px-8">
            <InertiaLink
                :href="route('dashboard')"
                class="brand-main font-base hidden text-lg font-black not-italic tracking-wider text-white md:block"
            >
                <img
                    class="h-14 w-auto"
                    src="/images/logo.png"
                    alt="Maat Stalling"
                />
            </InertiaLink>

            <button
                type="button"
                class="flex min-h-11 min-w-11 cursor-pointer items-center justify-center overflow-visible rounded-sm border-0 bg-white ease-linear hover:bg-gray-100 md:ml-0 md:hidden"
                @click="sidebarOpen = true"
            >
                <span class="sr-only">Open sidebar</span>
                <IconMenu2
                    class="size-6 text-gray-500"
                    aria-hidden="true"
                />
            </button>
        </div>

        <div class="flex items-stretch h-14 md:h-16">
            <button
                type="button"
                class="flex min-h-11 items-center justify-center gap-2 px-4 text-white/80 transition hover:text-white"
                @click="openSearch"
            >
                <IconSearch
                    class="size-5 shrink-0"
                    aria-hidden="true"
                />

                <kbd class="hidden rounded bg-white/15 px-1.5 py-0.5 text-xs font-medium text-white/80 lg:inline-block">
                    ⌘K
                </kbd>
            </button>

            <div
                class="hidden h-full md:block md:w-px md:bg-white/10"
                aria-hidden="true"
            />

            <Dropdown class="transition hover:bg-primary-500">
                <template #trigger>
                    <button
                        type="button"
                        class="h-full items-center px-4 py-2.5 text-white hover:text-gray-200 md:px-6"
                    >
                        <span class="sr-only">Add item</span>
                        <IconApps
                            class="size-6"
                            aria-hidden="true"
                        />
                    </button>
                </template>

                <template #content>
                    <DropdownItemGroup>
                        <DropdownItem
                            :href="route('clients.create')"
                            :icon="IconUserPlus"
                        >
                            Nieuwe klant
                        </DropdownItem>
                        <DropdownItem
                            :href="route('vehicles.create')"
                            :icon="IconDatabasePlus"
                        >
                            Nieuw voertuig
                        </DropdownItem>
                        <DropdownItem
                            :href="route('subscriptions.create')"
                            :icon="IconId"
                        >
                            Nieuwe reservering
                        </DropdownItem>
                    </DropdownItemGroup>
                </template>
            </Dropdown>

            <div
                class="hidden h-full lg:block lg:w-px lg:bg-white/10"
                aria-hidden="true"
            />

            <div class="flex w-full items-stretch">
                <Dropdown class="transition hover:bg-primary-500">
                    <template #trigger>
                        <button
                            type="button"
                            class="inline-flex items-center gap-3 px-4 py-3 md:pl-6 md:pr-8"
                        >
                            <span class="sr-only">Open menu</span>
                            <img
                                class="size-8 rounded-full bg-gray-50"
                                src="/images/avatar.png"
                                :alt="user.name"
                            />
                            <span class="hidden flex-col lg:flex lg:items-start text-sm">
                                <span
                                    class="font-semibold text-white"
                                    aria-hidden="true"
                                >
                                    {{ user.name }}
                                </span>
                                <span
                                    class="text-xs text-white"
                                    aria-hidden="true"
                                >
                                    {{ user.email }}
                                </span>
                            </span>
                        </button>
                    </template>

                    <template #content>
                        <UserDropdown :key="route().current()" />
                    </template>
                </Dropdown>
            </div>
        </div>
    </header>
</template>
